import classNames from 'classnames';
import React, { FC, useCallback, useState } from 'react';

import { Asset, InfoIcon } from '@clearcover/atlas';
import {
  GetChangeSummaryResponseType,
  GetPolicyDraftResponseType,
  PremiumChangesType,
} from '@clearcover/core-app-types';
import { Form, useFormikContext } from 'formik';
import { PolicyPaymentMethod } from 'Policies/components/PolicyPaymentMethod';
import { PolicySummaryPaymentFormValues } from 'Policies/screens/PolicyDraftSummary/PolicyDraftSummary';
import { Column, FormInput } from 'Shared/components';
import { paymentIcon } from 'Shared/constants/paymentIcon';
import { currency, formatMoney } from 'Shared/utils';
import { PaymentSchedule } from '../PaymentSchedule';
import styles from './styles.module.css';

function determinePaymentAmount(
  paymentFeeEnabledState: boolean,
  amountDue: PremiumChangesType['amountDue'],
  paymentMethodType: 'creditCard' | 'bank',
  draftType: GetPolicyDraftResponseType['policyDraft']['draftType']
): string {
  switch (true) {
    case draftType === 'renewal_quote':
      return '0';
    case paymentFeeEnabledState:
      return amountDue[paymentMethodType];
    default:
      return amountDue.raw;
  }
}

export const PaymentAndChangesSection: FC<{
  policySummaryResponse: GetChangeSummaryResponseType;
  draftType: GetPolicyDraftResponseType['policyDraft']['draftType'];
}> = ({ policySummaryResponse, draftType }) => {
  const { setFieldValue, values, handleChange } = useFormikContext<
    PolicySummaryPaymentFormValues
  >();
  const {
    paymentInfo: { isFullPay },
    premiumChanges,
    paymentFeeInfo: { paymentFeeEnabledState },
    paymentInfo,
    paymentScheduleChanges,
  } = policySummaryResponse;
  const initialPaymentOption =
    draftType === 'renewal_quote'
      ? 'No Payment'
      : isFullPay
      ? 'Next Bill'
      : 'No Payment';
  const [paymentOption, setPaymentOption] = useState(initialPaymentOption);

  const paymentMethodType = paymentInfo.hasRefund
    ? 'creditCard'
    : paymentInfo?.paymentMethods?.find(pm => pm.id === values.paymentMethodId)
        ?.type || 'creditCard';

  const setPaymentAmount = useCallback(
    (selectedOption: string, selectedPaymentMethod: 'creditCard' | 'bank') => {
      const paymentAmount: number =
        selectedOption === 'No Payment'
          ? 0
          : currency(
              determinePaymentAmount(
                paymentFeeEnabledState,
                premiumChanges.amountDue,
                selectedPaymentMethod,
                draftType
              )
            ).value;

      setFieldValue('paymentAmount', paymentAmount);
    },
    [draftType, paymentFeeEnabledState, premiumChanges.amountDue, setFieldValue]
  );

  return (
    <>
      <div className={styles.premiumContainer}>
        <div className={styles.paymentScheduleContainer}>
          <div className={styles.summaryHeader}>New Payment Schedule</div>
          <PaymentSchedule paymentScheduleChanges={paymentScheduleChanges} />
        </div>
        <div className={styles.billedAmountsContainer}>
          <div className={styles.summaryHeader}>New Billed Amounts</div>
          <div data-testid="policy-summary-premium-change-section">
            <div className={styles.premiumTotals}>
              <div className={styles.premiumLabels}>
                <div className={styles.premiumTotalsHeader}>
                  Written Premium
                </div>
                <div className={styles.premiumTotalsHeader}>Total Cost</div>
                <div className={styles.premiumTotalsHeader}>
                  Monthly Payment Amount
                </div>
                <div className={styles.premiumTotalsHeader}>
                  Amount Due Today
                </div>
              </div>
              <div
                data-testid="premium-change-values"
                className={styles.premiumValues}
              >
                <div
                  data-testid="new-written-premium"
                  className={styles.premiumTotalValue}
                >
                  {formatMoney(premiumChanges.newWrittenPremium)}
                </div>
                <div
                  data-testid="new-total-cost"
                  className={styles.premiumTotalValue}
                >
                  {formatMoney(premiumChanges.newTotalCost)}
                </div>
                <div
                  data-testid="new-monthly-payment"
                  className={styles.premiumTotalValue}
                >
                  {formatMoney(premiumChanges.newMonthlyPaymentAmount)}
                </div>
                <div
                  data-testid="amount-prior-to-endorsement"
                  className={styles.premiumTotalValue}
                >
                  {formatMoney(
                    determinePaymentAmount(
                      paymentFeeEnabledState,
                      premiumChanges.amountDue,
                      paymentMethodType,
                      draftType
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <>
        {policySummaryResponse.paymentInfo.hasRefund ? (
          <div
            className={`${styles.premiumContainer} ${styles.refundContainer}`}
            data-testid="summary-refund-section"
          >
            <Column className={styles.refundCol}>
              <div className={styles.summaryHeader}>Refund Due</div>
              <div className={styles.refundBanner}>
                <Asset
                  icon={InfoIcon}
                  description="refund"
                  height={16}
                  width={16}
                  className={styles.helpIcon}
                />
                <span>
                  Payment will be automatically refunded to the default payment
                  method on file for the customer. If the refund fails to post
                  to that account, customer will receive a check in the mail in
                  7-14 days.
                </span>
              </div>
            </Column>
            <Column className={styles.refundCol}>
              <div className={styles.refundRow}>
                <h3>Refund Method</h3>
                <div className={styles.refundPaymentMethod}>
                  <Asset
                    icon={
                      paymentIcon[
                        policySummaryResponse.paymentInfo.defaultPaymentMethod
                          .type === 'creditCard'
                          ? policySummaryResponse.paymentInfo
                              .defaultPaymentMethod.cardType
                          : 'bank'
                      ]
                    }
                    description="credit card"
                    height={16}
                    width={16}
                  />
                  <span>{`•••• •••• •••• ${policySummaryResponse.paymentInfo.defaultPaymentMethod.lastFour}`}</span>
                </div>
              </div>
              <div className={styles.refundRow}>
                <h3>Estimated Refund Amount</h3>
                {policySummaryResponse.paymentInfo.hasRefund && (
                  <h3>
                    {currency(
                      Math.abs(policySummaryResponse.paymentInfo.refundAmount)
                    ).format()}
                  </h3>
                )}
              </div>
            </Column>
          </div>
        ) : (
          <Form>
            <div className={styles.paymentTypeMethodAmountContainer}>
              <div className={styles.payment}>
                <div className={styles.summaryHeader}>Payment Type</div>
                <label className={styles.paymentLabel}>Choose an option</label>
                <FormInput
                  variant="radioGroup"
                  name="payment-type"
                  data-testid="payment-type"
                  className={styles.paymentRadioGroup}
                  onChange={(newPaymentOption: string) => {
                    setPaymentOption(newPaymentOption);
                    setPaymentAmount(newPaymentOption, paymentMethodType);
                  }}
                  value={paymentOption}
                  label=""
                  options={
                    draftType === 'renewal_quote'
                      ? { 'No Payment': 'No Payment' }
                      : {
                          'No Payment': 'No Payment',
                          'Next Bill': 'Next Bill',
                        }
                  }
                />
              </div>
              <div
                className={styles.payment}
                data-testid="payment-method-section"
              >
                <div className={styles.summaryHeader}>Payment Method</div>
                <label className={styles.paymentLabel}>
                  Select Payment Type
                </label>
                <div className={styles.paymentMethodPicker}>
                  <PolicyPaymentMethod
                    policyPaymentMethods={
                      policySummaryResponse.paymentInfo.hasRefund
                        ? []
                        : policySummaryResponse.paymentInfo.paymentMethods
                    }
                    value={values.paymentMethodId}
                    setValue={newPaymentMethodId => {
                      const newPaymentMethodType = paymentInfo.hasRefund
                        ? 'creditCard'
                        : paymentInfo?.paymentMethods?.find(
                            pm => pm.id === newPaymentMethodId
                          )?.type || 'creditCard';
                      setPaymentAmount(paymentOption, newPaymentMethodType);
                      setFieldValue('paymentMethodId', newPaymentMethodId);
                    }}
                    disabled={paymentOption === 'No Payment'}
                  />
                </div>
              </div>
              <div className={classNames(styles.payment, styles.paymentAmount)}>
                <div className={styles.summaryHeader}>Payment Amount</div>
                <label className={styles.paymentLabel}>
                  Enter Payment Amount
                </label>
                <FormInput
                  variant="text"
                  name="paymentAmount"
                  data-testid="payment-amount"
                  onChange={handleChange}
                  value={formatMoney(values.paymentAmount)}
                  label="Payment Amount"
                  disabled={true}
                />
              </div>
            </div>
          </Form>
        )}
      </>
    </>
  );
};
